/**
 * Application settings modal on editor page
 */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled from 'styled-components'
import _ from 'lodash'
import { Input, Tooltip, Slider, ToggleSwitch } from 'wix-style-react'
import Tabs from 'wix-style-react/src/Tabs'

import MessageBoxFunctionalLayout from 'components/shared/MessageBoxFunctionalLayout'
import * as integrationType from 'constants/integrationType'
import ShopifyConnect from '../integrations/ShopifyConnect'
import { setCustomFieldArray, setNumberOfSlides, changeAppName } from 'controllers/logic'
import UploadButton from './assetsManager/UploadButton'
import { getTemplateInfo } from '../../templates/templates'
import { IA_HOST_NAME } from 'constants/index'
import AppNameInput from 'components/shared/AppNameInput'
import Text from 'components/common/Text'

const SettingsBox = styled.div`
  height: 26em;
  overflow: auto;
  ::-webkit-scrollbar {
      width: 12px;
  }
  ::-webkit-scrollbar-track {
      -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.1);
      border-radius: 10px;
      background-color: white;
  }
  ::-webkit-scrollbar-thumb {
      border-radius: 15px;
      -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.15);
  }
`
const IntegrationsBox = styled.div`
  padding-top: 30px;
  height: 26em;
  overflow: auto;
`
const TitleInput = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 5px;
  width: 100%;
`
const FaviconPicker = styled.div`
  _display: inline-block;
  padding: 10px 5px;
  width: 70%;
  display: flex;
  flex-direction: column;
`
const SliderContainer = styled.div`
  display: inline-block;
  padding: 20px 5px;
  width: 70%;
`
const AppNameInputContainer = styled.div`
  margin-top: 1em;
  padding: 10px 5px;
`
const SkipSlidesContainer = styled.div`
  margin-top: 3em;
`
const ToggleSwitchContainer = styled.div`
  display: flex;
  & span {
    margin-left: 1em;
  }
`

const items = [
  {id: 1, title: 'Instant App Settings'},
  {id: 2, title: 'Integrations'},
]

class SettingsView extends Component {

  constructor (props) {
    super(props)

    const info = getTemplateInfo(props.app.templateId)

    this.state = {
      activeId: props.modalActiveTab,
      title: pickupTitle(props.app),
      googleAnalyticsId: pickupGoogleAnalyticsId(props.app),
      favicon: pickupFaviconUrl(props.app),
      displayFaviconSpin: false,
      redirectUrl: pickupRedirectUrl(props.app),
      numberOfSlides: (props.app.slidesCnt) ? props.app.slidesCnt : info.defaultSlides,
      appName: props.app.name,
      skipSlides: pickupSkipSlides(props.app),
      autoRedirectEnabled: pickupAutoRedirectEnabled(props.app)
    }
  }

  componentWillReceiveProps (nextProps) {
    const displayFaviconSpin =
      this.state.displayFaviconSpin &&
      pickupFaviconUrl(nextProps.app) !== pickupFaviconUrl(this.props.app)

    const info = getTemplateInfo(nextProps.app.templateId)

    this.setState({
      title: pickupTitle(nextProps.app),
      googleAnalyticsId: pickupGoogleAnalyticsId(nextProps.app),
      favicon: pickupFaviconUrl(nextProps.app),
      displayFaviconSpin,
      redirectUrl: pickupRedirectUrl(nextProps.app),
      numberOfSlides: (nextProps.app.slidesCnt) ? nextProps.app.slidesCnt : info.minSlides,
      appName: nextProps.app.name,
      skipSlides: pickupSkipSlides(nextProps.app),
      autoRedirectEnabled: pickupAutoRedirectEnabled(nextProps.app)
    })
  }

  onItemClick = (item) => {
    this.setState({
      activeId: item.id
    })
  }

  saveSetting (settingName, settingValue) {
    const { dispatch, app } = this.props
    dispatch(setCustomFieldArray(app.id, ['appSettings', settingName], settingValue))
  }

  onOk = () => {
    const { dispatch, app } = this.props
    this.saveSetting('title', this.state.title)
    this.saveSetting('googleAnalyticsId', this.state.googleAnalyticsId)
    this.saveSetting('favicon', this.state.favicon)
    this.saveSetting('redirectUrl', this.state.redirectUrl)
    this.saveSetting('autoRedirectEnabled', this.state.autoRedirectEnabled)
    if (this.state.numberOfSlides !== app.slidesCnt) {
      dispatch(setNumberOfSlides(app, this.state.numberOfSlides))
    }
    if (this.state.appName !== app.name) {
      dispatch(changeAppName(app, this.state.appName))
    }

    const info = getTemplateInfo(app.templateId)
    const skipSlidesInfo = _.get(info, 'skipSlides', {})

    for (const skipSlideId of this.state.skipSlides) {
      const sInfo = skipSlidesInfo[skipSlideId]
      dispatch(setCustomFieldArray(app.id, sInfo.path, null))
    }
    this.saveSetting('skipSlides', this.state.skipSlides)
    // console.log('onOK', this.state)
    this.props.close()
  }

  onTitleInput = (e) => this.setState({ title: e.target.value })
  onGoogleAnalyticsIdInput = (e) => this.setState({ googleAnalyticsId: e.target.value })

  renderTitleInput () {
    return (
      <Tooltip
        content='Title to be shown in browser'
        onClickOutside={null}
        onShow={() => null}
        shouldCloseOnClickOutside
        size='large'
        placement='right'
      >
        <TitleInput>
          <Text>App Title</Text>
          <Input
            size='normal'
            onChange={this.onTitleInput}
            value={this.state.title}
          />
        </TitleInput>
      </Tooltip>
    )
  }

  onRedirectUrlInput = (e) => this.setState({ redirectUrl: e.target.value })

  handleEnableAutoRedirect = (e) => {
    const { checked } = e.target
    console.log('handleEnableAutoRedirect', checked)
    this.setState({autoRedirectEnabled: checked})
  }

  renderAutoRedirectSwitch = () => {
    const { app } = this.props
    const info = getTemplateInfo(app.templateId)
    if (!_.isEmpty(this.state.redirectUrl) && info.canDisableRedirectOnSwipe) {
      return (
        <TitleInput>
          <ToggleSwitchContainer>
            <ToggleSwitch
              checked={this.state.autoRedirectEnabled}
              onChange={this.handleEnableAutoRedirect}
            />
            <Text>Enable auto redirect on the last page</Text>
          </ToggleSwitchContainer>
        </TitleInput>
      )
    }
  }

  renderRedirectUrlInput () {
    return (
      <Tooltip
        content='If a non-mobile device is detected, the user will be redirected to this URL.'
        onClickOutside={null}
        onShow={function noRefCheck () {}}
        shouldCloseOnClickOutside
        size='large'
        placement='right'
      >
        <TitleInput>
          <Text>Redirect desktop users to this URL:</Text>
          <Input
            size='normal'
            onChange={this.onRedirectUrlInput}
            value={this.state.redirectUrl}
          />
        </TitleInput>
      </Tooltip>
    )
  }

  renderFaviconPicker () {
    const { app } = this.props
    const faviconUrl = _.get(app, `assets.${app.id}_favicon.url`, null)
    return (
      <FaviconPicker>
        <Text>Add Favicon</Text>
        <UploadButton
          appId={app.id}
          fileId={`${app.id}_favicon`}
          filetypes={'image/jpeg,image/png'}
          imageUrl={faviconUrl}
        />
      </FaviconPicker>
    )
  }

  onChangeNumberOfScenes = (v) => {
    this.setState({
      numberOfSlides: v[0]
    })
  }

  renderNumberOfSlides = () => {
    const { app } = this.props
    const info = getTemplateInfo(app.templateId)
    if (info.minSlides && info.maxSlides) {
      return (
        <SliderContainer>
          <Text># of scenes</Text>
          <Slider
            min={info.minSlides}
            max={info.maxSlides}
            value={[this.state.numberOfSlides]}
            onChange={this.onChangeNumberOfScenes}
          />
        </SliderContainer>
      )
    }
  }

  onChangeAppName = (newName) => {
    console.log('onChangeAppName', newName)
    this.setState({
      appName: (newName === '') ? this.props.app.name : newName
    })
  }

  handleSkipSlide = (id) => {
    return (evt) => {
      const { checked } = evt.target
      const { skipSlides } = this.state
      const skipSlidesCopy = _.clone(skipSlides)
      const index = _.indexOf(skipSlidesCopy, id)
      if (index >= 0) skipSlidesCopy.splice(index, 1)
      if (checked) skipSlidesCopy.push(id)
      this.setState({
        skipSlides: skipSlidesCopy
      })
    }
  }

  renderSkippedSlideSwitch = (slide, id) => {
    const { skipSlides } = this.state
    const skipped = _.indexOf(skipSlides, id) >= 0
    return (
      <ToggleSwitchContainer key={id}>
        <ToggleSwitch
          checked={skipped}
          onChange={this.handleSkipSlide(id)}
        />
        <Text>{slide.name}</Text>
      </ToggleSwitchContainer>
    )
  }


  renderSkipSlides= () => {
    const info = getTemplateInfo(this.props.app.templateId)
    const skipSlidesInfo = _.get(info, 'skipSlides', {})
    if (!_.isEmpty(skipSlidesInfo)) {
      return (
        <SkipSlidesContainer>
          {_.map(skipSlidesInfo, this.renderSkippedSlideSwitch)}
        </SkipSlidesContainer>
      )
    }

    return null
  }

  renderAppSettings () {
    const { app } = this.props
    // const appUrl = `${IA_HOST_NAME}/${app.name}`
    return (
      <SettingsBox>
        <Tooltip
          content='This is the live link of your published instant app.'
          onClickOutside={null}
          onShow={() => null}
          shouldCloseOnClickOutside
          size='large'
          placement='right'
        >
          <AppNameInputContainer>
            <AppNameInput
              onChangeAppName={this.onChangeAppName}
              appName={app.name}
              required={false}
              title={'Published App URL'}
              showLInk={false}
              prefix={IA_HOST_NAME}
            />
          </AppNameInputContainer>
        </Tooltip>
        {this.renderTitleInput()}
        {this.renderRedirectUrlInput()}
        {this.renderAutoRedirectSwitch()}
        {this.renderFaviconPicker()}
        {/* {this.renderNumberOfSlides()} */}
        {this.renderSkipSlides()}
      </SettingsBox>
    )
  }

  renderIntegration () {
    const { app } = this.props
    const integration = _.get(app, 'customFields.appSettings.integration', {})
    if (!_.isEmpty(integration)) {
      switch (integration.type) {
        case integrationType.SHOPIFY:
          return (
            <ShopifyConnect
              appId={app.id}
              shopName={_.get(integration, ['shopName'])}
              accessToken={_.get(integration, ['accessToken'])}
              showDivider={false}
            />
          )
        default:
          return null
      }
    }
  }

  renderIntegrationSettings () {
    return (
      <IntegrationsBox>
        {this.renderIntegration()}
        {/* <TitleInput>
          <Text>Google Analytics ID</Text>
          <Input
            size='normal'
            value={this.state.googleAnalyticsId}
            onChange={this.onGoogleAnalyticsIdInput}
          />
        </TitleInput> */}
      </IntegrationsBox>
    )
  }

  renderContent () {
    switch (this.state.activeId) {
      case 1:
        return this.renderAppSettings()
      case 2:
        return this.renderIntegrationSettings()
      default:
        return null
    }
  }

  render () {
    return (
      <MessageBoxFunctionalLayout
        theme='blue'
        title='Settings'
        confirmText='Save'
        cancelText='Cancel'
        onOk={this.onOk}
        onCancel={this.props.close}
        width='40em'
      >
        <Tabs
          items={items}
          activeId={this.state.activeId}
          onClick={this.onItemClick}
        />
        {this.renderContent()}
      </MessageBoxFunctionalLayout>
    )
  }
}

function pickupTitle (app) {
  return _.get(app, 'customFields.appSettings.title', 'Instant App')
}

function pickupGoogleAnalyticsId (app) {
  return _.get(app, 'customFields.appSettings.googleAnalyticsId', '')
}

function pickupFaviconUrl (app) {
  return _.get(app, `assets.${app.id}_favicon.url`, null)
}

function pickupRedirectUrl (app) {
  return _.get(app, 'customFields.appSettings.redirectUrl', '')
}

function pickupSkipSlides (app) {
  return _.get(app, 'customFields.appSettings.skipSlides', [])
}

function pickupAutoRedirectEnabled (app) {
  return _.get(app, 'customFields.appSettings.autoRedirectEnabled', true)
}

SettingsView.propTypes = {
  close: PropTypes.func.isRequired,
  modalActiveTab: PropTypes.number.isRequired,
  app: PropTypes.object.isRequired
}

export default connect()(SettingsView)



// WEBPACK FOOTER //
// ./src/components/editor/SettingsView.js